import { Cell, CellChange, Row } from '@silevis/reactgrid';
import { round } from 'lodash';
import { PaymentEmailStatus } from 'further-types/investment';
import { InvestmentPaymentType } from 'further-types/payment';

import { columns } from './columns-v2';

export type ColumnDefinition = (typeof columns)[number];

const PAYMENT_EMAIL_STATUS_PAID_VALUES: Array<PaymentEmailStatus> = [
  PaymentEmailStatus.PaidEmail,
  PaymentEmailStatus.PaidNoEmail,
  PaymentEmailStatus.PaidDisableEmail,
];

const isPaymentEmailStatus = (
  value: GridCellValue,
): value is PaymentEmailStatus => {
  if (typeof value !== 'string') return false;
  return PAYMENT_EMAIL_STATUS_PAID_VALUES.includes(value as PaymentEmailStatus);
};

const filterToPaidPaymentEmailOptions = (options: Array<DropdownOption>) =>
  options.filter((opt) => isPaymentEmailStatus(opt.value));

export const isRowFullyPaidByCashBalance = (rowData: GridRow) => {
  if (rowData.use_cash_balance !== 'true') {
    return false;
  }

  const requiredTotal = round(
    (Number(rowData.investment_amount) || 0) +
      (Number(rowData.advice_fee) || 0),
    2,
  );
  const cashBalance = round(Number(rowData.cash_balance), 2);

  return cashBalance > 0 && cashBalance >= requiredTotal;
};

export type GridCellValue = string | number | boolean | null | undefined;
export type GridRow = Record<string, GridCellValue>;
export type GridData = Array<GridRow>;
export const GRID_DATA_STORAGE_KEY = 'investorUploadGridDataV2';

type DropdownOption = {
  label: string;
  value: boolean | number | string;
};
type DropdownData = Record<string, Array<DropdownOption> | undefined>;
type Column = ColumnDefinition;

const POSTCODE_COLUMN_IDS = new Set([
  'postcode',
  'prev_address_a_postcode',
  'prev_address_b_postcode',
  'prev_address_c_postcode',
  'leu_postcode',
]);

export const createInitialGridData = (count = 1): GridData =>
  Array.from({ length: count }, (_, k) => getEmptyRow(k));

export const buildRowsFromGridData = (
  gridData: GridData,
  gridDropdownsData: DropdownData,
  columnsOverride?: Array<ColumnDefinition>,
): Array<Row<Cell>> => {
  const colsToUse = columnsOverride ?? columns;

  const headerRow: Row<Cell> = {
    rowId: 'header',
    cells: colsToUse.map((column) => ({
      type: 'header',
      text: `${column.header}${column.required ? '*' : ''}`,
      style: column.style,
      className: column.className,
    })),
  };

  const dataRows: Array<Row<Cell>> = gridData.map((rowData) => {
    const parsedIndex = parseInt(String(rowData.index ?? '0'), 10);
    const rowId =
      Number.isNaN(parsedIndex) || parsedIndex <= 0 ? 0 : parsedIndex - 1;

    return {
      rowId,
      cells: colsToUse.map((column) =>
        createCell(column, rowData, gridDropdownsData),
      ),
    };
  });

  return [headerRow, ...dataRows];
};

const createCell = (
  column: Column,
  rowData: GridRow,
  gridDropdownsData: DropdownData,
): Cell => {
  const value = rowData[column.columnId];

  switch (column.type) {
    case 'custom_dropdown': {
      const baseValues: Array<DropdownOption> =
        gridDropdownsData[column.columnId] ?? column.values ?? [];
      let valuesArray: Array<DropdownOption> = baseValues;
      if (
        column.columnId === 'payment_email_status' &&
        isRowFullyPaidByCashBalance(rowData)
      ) {
        valuesArray = filterToPaidPaymentEmailOptions(baseValues);
      } else if (column.columnId === 'paid_to') {
        if (!isRowFullyPaidByCashBalance(rowData)) {
          valuesArray = baseValues.filter(
            (opt) => opt.value !== InvestmentPaymentType.CashBalance,
          );
        }
      }
      let selectedValue =
        value === null || value === undefined || value === ''
          ? ''
          : String(value);
      if (
        column.columnId === 'paid_to' &&
        selectedValue === InvestmentPaymentType.CashBalance &&
        !valuesArray.some(
          (opt) => opt.value === InvestmentPaymentType.CashBalance,
        )
      ) {
        selectedValue = '';
      }
      return {
        ...column,
        type: 'custom_dropdown',
        value: Number.NaN,
        text: selectedValue,
        values: valuesArray,
        nonEditable:
          column.columnId === 'paid_to' && isRowFullyPaidByCashBalance(rowData),
      };
    }

    case 'email':
      return {
        ...column,
        type: 'email',
        text: value != null ? String(value) : '',
      };

    case 'date': {
      const date = value
        ? new Date(String(value).split('/').reverse().join('-'))
        : undefined;
      return { ...column, type: 'date', value, date };
    }

    case 'number':
      return { ...column, type: 'number', value };

    default:
      return {
        ...column,
        type: column.type || 'text',
        text: value != null ? String(value) : '',
      };
  }
};

export const getEmptyRow = (k: number): GridRow =>
  columns.reduce(
    (acc, column) => ({
      ...acc,
      [column.columnId]: column.columnId === 'index' ? `${k + 1}` : '',
    }),
    {},
  );

export const applyChangesToPrevRows = (
  changes: Array<CellChange>,
  prevRowData: GridData,
): GridData => {
  const newRowData = prevRowData.map((row) => ({ ...row }));

  changes.forEach(({ rowId, columnId, type, newCell }) => {
    const rowIndex =
      typeof rowId === 'number' ? rowId : parseInt(String(rowId), 10) || 0;

    // Ensure row exists
    if (!newRowData[rowIndex]) {
      newRowData[rowIndex] = getEmptyRow(rowIndex);
    }

    const columnKey = String(columnId);
    const rowData = newRowData[rowIndex];

    // Extract new value based on cell type
    let newValue = extractCellValue(type, newCell);
    if (typeof newValue === 'string' && POSTCODE_COLUMN_IDS.has(columnKey)) {
      newValue = newValue.toUpperCase();
    }
    rowData[columnKey] = newValue;
  });

  return newRowData;
};

const extractCellValue = (
  type: string,
  cell: CellChange['newCell'],
): GridCellValue => {
  if (type === 'checkbox' && 'checked' in cell) {
    if (typeof cell.checked === 'boolean') {
      return cell.checked;
    }
    return undefined;
  }

  if (type === 'number' && 'value' in cell) {
    if (cell.value === null) return null;
    if (
      typeof cell.value === 'string' ||
      typeof cell.value === 'number' ||
      typeof cell.value === 'boolean'
    ) {
      return cell.value;
    }
    return undefined;
  }

  if (type === 'custom_dropdown' && 'text' in cell) {
    if (typeof cell.text !== 'string') return undefined;
    const t = cell.text.trim();
    if (t === '') return '';
    return t;
  }

  if ('text' in cell && typeof cell.text === 'string') {
    return cell.text.trim();
  }

  return undefined;
};
